import { SWRFetcher } from '@commenda/utils';
import useSWR, { type SWRConfiguration } from 'swr';
import {
  ListTransactionsParams,
  SalesTaxTransactionExtend,
  SortDirection,
  TransactionAggregates,
  TransactionListResponse,
} from './types';

// These transaction reads are expensive even when the response later resolves
// to 304, because the API computes the result before Express handles ETags.
// Avoid SWR focus-triggered bursts and keep data fresh with bounded polling.
const TRANSACTION_LIST_SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshWhenHidden: false,
  dedupingInterval: 30_000,
  refreshInterval: 60_000,
};

const TRANSACTION_AGGREGATE_SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshWhenHidden: false,
  dedupingInterval: 60_000,
  refreshInterval: 5 * 60_000,
};

// Helper function to join array or return the value directly
const formatArrayToString = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value;
};

export function useSalesTaxTransactions({
  token,
  filters,
  cursor = null,
  limit = 10,
  showTaxBreakdown = false,
  sortState,
}: ListTransactionsParams) {
  const query = new URLSearchParams();

  if (filters.corporationId) {
    query.append('corporationId', filters.corporationId.toString());
  }

  if (cursor) {
    query.append('cursor', cursor);
  }

  // ADD FILTERS
  if (filters.from_date) {
    query.append('from_date', filters.from_date);
  }
  if (filters.to_date) {
    query.append('to_date', filters.to_date);
  }
  if (filters.reporting_date_override_from_date) {
    query.append(
      'reporting_date_override_from_date',
      filters.reporting_date_override_from_date
    );
  }
  if (filters.reporting_date_override_to_date) {
    query.append(
      'reporting_date_override_to_date',
      filters.reporting_date_override_to_date
    );
  }
  if (filters.creation_methods) {
    const creationMethodFilter = formatArrayToString(filters.creation_methods);
    query.append('creation_methods', creationMethodFilter);
  }

  if (filters.source_platforms) {
    const sourcePlatformFilter = formatArrayToString(filters.source_platforms);
    query.append('source_platforms', sourcePlatformFilter);
  }

  if (filters.destination_countries) {
    const destinationCountriesFilter = formatArrayToString(
      filters.destination_countries
    );
    query.append('destination_countries', destinationCountriesFilter);
  }

  if (filters.destination_states) {
    const destinationStatesFilter = formatArrayToString(
      filters.destination_states
    );
    query.append('destination_states', destinationStatesFilter);
  }

  if (filters.statuses) {
    const statusesFilter = formatArrayToString(filters.statuses);
    query.append('statuses', statusesFilter);
  }

  if (filters.currency) {
    const currencyFilter = formatArrayToString(filters.currency);
    query.append('currency', currencyFilter);
  }

  if (filters.target_currency) {
    query.append('target_currency', filters.target_currency);
  }

  if (filters.search_term) {
    query.append('search_term', filters.search_term);
  }

  if (filters.type) {
    query.append('type', filters.type);
  }

  if (filters.marketplaces) {
    const marketplacesFilter = formatArrayToString(filters.marketplaces);
    query.append('marketplaces', marketplacesFilter);
  }

  if (sortState) {
    query.append('sort_by', `${sortState.id}`);
    query.append(
      'sort_order',
      `${sortState.desc ? SortDirection.DESC : SortDirection.ASC}`
    );
  }

  query.append('limit', limit.toString());
  if (showTaxBreakdown) {
    query.append('extend', `${SalesTaxTransactionExtend.SHIPPING_ADDRESS}`);
  } else {
    query.append(
      'extend',
      `${SalesTaxTransactionExtend.SHIPPING_ADDRESS},${SalesTaxTransactionExtend.CUSTOMER}`
    );
  }

  const { data, error, isLoading, mutate } = useSWR<TransactionListResponse>(
    `new-sales-tax/transactions?${query}`,
    SWRFetcher(
      {
        method: 'GET',
      },
      token
    ),
    TRANSACTION_LIST_SWR_OPTIONS
  );

  return {
    data: data
      ? {
          transactions: data.data,
          nextCursor: data.nextCursor,
          totalTransactions: data.totalTransactions,
        }
      : {
          transactions: [],
          nextCursor: null,
          totalTransactions: 0,
        },
    error,
    mutate,
    isLoading,
  };
}

export function useSalesTaxTransactionAggregates({
  token,
  filters,
  sortState,
}: ListTransactionsParams) {
  const query = new URLSearchParams();

  if (filters.corporationId) {
    query.append('corporationId', filters.corporationId.toString());
  }

  // ADD FILTERS
  if (filters.from_date) query.append('from_date', filters.from_date);
  if (filters.to_date) query.append('to_date', filters.to_date);
  if (filters.reporting_date_override_from_date) {
    query.append(
      'reporting_date_override_from_date',
      filters.reporting_date_override_from_date
    );
  }
  if (filters.reporting_date_override_to_date) {
    query.append(
      'reporting_date_override_to_date',
      filters.reporting_date_override_to_date
    );
  }

  if (filters.creation_methods) {
    const creationMethodFilter = formatArrayToString(filters.creation_methods);
    query.append('creation_methods', creationMethodFilter);
  }

  if (filters.source_platforms) {
    const sourcePlatformFilter = formatArrayToString(filters.source_platforms);
    query.append('source_platforms', sourcePlatformFilter);
  }

  if (filters.statuses) {
    const statusesFilter = formatArrayToString(filters.statuses);
    query.append('statuses', statusesFilter);
  }

  if (filters.destination_countries) {
    const destinationCountriesFilter = formatArrayToString(
      filters.destination_countries
    );
    query.append('destination_countries', destinationCountriesFilter);
  }

  if (filters.destination_states) {
    const destinationStatesFilter = formatArrayToString(
      filters.destination_states
    );
    query.append('destination_states', destinationStatesFilter);
  }

  if (filters.currency) {
    const currencyFilter = formatArrayToString(filters.currency);
    query.append('currency', currencyFilter);
  }

  if (filters.target_currency) {
    query.append('target_currency', filters.target_currency);
  }

  if (filters.search_term) {
    query.append('search_term', filters.search_term);
  }

  if (filters.type) {
    query.append('type', filters.type);
  }

  if (filters.marketplaces) {
    const marketplacesFilter = formatArrayToString(filters.marketplaces);
    query.append('marketplaces', marketplacesFilter);
  }

  if (sortState) {
    query.append('sort_by', `${sortState.id}`);
    query.append(
      'sort_order',
      `${sortState.desc ? SortDirection.DESC : SortDirection.ASC}`
    );
  }

  const { data, error, isLoading, mutate } = useSWR<TransactionAggregates>(
    `new-sales-tax/transactions/aggregate?${query}`,
    SWRFetcher(
      {
        method: 'GET',
      },
      token
    ),
    TRANSACTION_AGGREGATE_SWR_OPTIONS
  );

  // Default values for first paint while SWR is loading. Currency, dateGroups
  // and FX counters omitted intentionally — only meaningful once the server
  // responds.
  const defaultAggregates: TransactionAggregates = {
    totalTransactions: 0,
    totalSales: 0,
    totalTaxCollected: 0,
    totalPurchases: 0,
    totalTaxPaid: 0,
  };

  return {
    aggregates: data || defaultAggregates,
    error,
    mutate,
    isLoading,
  };
}
